/**
 * PostHog analytics wrapper for FutureSelf.
 *
 * Privacy rules (do NOT relax without explicit user approval):
 *  - autocapture: OFF
 *  - session_recording: OFF
 *  - Only the 6 approved events are exposed (see EVENTS below).
 *  - NEVER pass user PII (email, name, journal/gratitude/goal text, affirmations,
 *    community posts, photos, videos) as event properties or user properties.
 *  - Identify the user by the internal database user_id only.
 */

import posthog from 'posthog-js';

const KEY = process.env.REACT_APP_POSTHOG_KEY;
const HOST = process.env.REACT_APP_POSTHOG_HOST || 'https://eu.i.posthog.com';

let initialized = false;

// Consent gate — PostHog MUST NOT initialise until the user has explicitly
// accepted optional analytics. Choice is persisted in localStorage under
// `fs_analytics_consent`: 'granted' | 'declined' | (unset).
const CONSENT_STORAGE_KEY = 'fs_analytics_consent';

export const getAnalyticsConsent = () => {
  if (typeof window === 'undefined') return null;
  try {
    const v = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    return v === 'granted' || v === 'declined' ? v : null;
  } catch (_) {
    return null;
  }
};

export const setAnalyticsConsent = (choice) => {
  if (typeof window === 'undefined') return;
  const normalised = choice === 'granted' ? 'granted' : 'declined';
  try { window.localStorage.setItem(CONSENT_STORAGE_KEY, normalised); } catch (_) { /* quota / private mode */ }
  if (normalised === 'granted') {
    initAnalytics();
  } else {
    // If PostHog is already loaded, opt the user out so any queued events are dropped.
    if (initialized && typeof posthog.opt_out_capturing === 'function') {
      try { posthog.opt_out_capturing(); } catch (_) { /* noop */ }
    }
    initialized = false;
    if (typeof window !== 'undefined') {
      // Keep the observability flag in sync with reality for devtools debugging.
      window.__posthog_ready = {
        ...(window.__posthog_ready || {}),
        initialized: false,
        reason: 'consent_revoked',
      };
    }
  }
};

// Set an early readiness flag so we can inspect state from browser devtools
// even when the key is missing. NEVER expose the key value itself.
if (typeof window !== 'undefined') {
  window.__posthog_ready = {
    initialized: false,
    has_key: !!KEY,
    host: HOST,
    // Which env var name family is present (CRA vs Vite mistake diagnostic)
    env_prefix: 'REACT_APP_',
  };
}

export const initAnalytics = () => {
  if (initialized) return;
  // GDPR gate: never initialise PostHog unless the user has explicitly granted consent.
  if (getAnalyticsConsent() !== 'granted') {
    if (typeof window !== 'undefined') {
      window.__posthog_ready = {
        ...(window.__posthog_ready || {}),
        initialized: false,
        has_key: !!KEY,
        host: HOST,
        reason: 'consent_not_granted',
      };
    }
    return;
  }
  if (!KEY) {
    // Safe diagnostic — key existence only, no value.
     
    console.info('[analytics] PostHog init skipped: has_key=false, host=' + HOST);
    if (typeof window !== 'undefined') {
      window.__posthog_ready = {
        ...(window.__posthog_ready || {}),
        initialized: false,
        has_key: false,
        host: HOST,
        reason: 'missing REACT_APP_POSTHOG_KEY',
      };
    }
    return; // Silently skip capture, but leave the diagnostic flag
  }

  posthog.init(KEY, {
    api_host: HOST,
    // Privacy hardening
    autocapture: false,
    capture_pageview: false,
    capture_pageleave: false,
    disable_session_recording: true,
    disable_surveys: true,
    disable_persistence: false, // Allow anonymous_id cookie for user continuity
    // Do not send any URL query strings or referrer data that could contain tokens
    sanitize_properties: (properties) => {
      // Strip any accidental URL parameters (e.g., ?token=..., session_id=...)
      const clean = { ...properties };
      delete clean.$current_url;
      delete clean.$pathname;
      delete clean.$referrer;
      delete clean.$referring_domain;
      return clean;
    },
    loaded: (ph) => {
      // Extra defence: opt out of any auto-behaviour that slipped through
      ph.set_config({ property_denylist: ['$ip', '$current_url', '$pathname'] });
    },
  });

  initialized = true;
  // Expose for browser-devtools debugging + our own tests
  if (typeof window !== 'undefined') {
    window.posthog = posthog;
    window.__posthog_ready = {
      initialized: true,
      has_key: true,   // boolean only, never the value
      host: HOST,
      env_prefix: 'REACT_APP_',
    };
  }
   
  console.info('[analytics] PostHog init: initialized=true, host=' + HOST + ', has_key=true');
};

// Whitelist of allowed event names — attempts to send anything else are dropped.
const ALLOWED_EVENTS = new Set([
  'account_created',
  'onboarding_completed',
  'visualisation_completed',
  'daily_routine_completed',
  'premium_page_viewed',
  'subscription_started',
  'premium_cancellation_requested',
  'premium_cancellation_reversed',
  'premium_subscription_ended',
]);

// Whitelist of PII-safe property keys. Anything else is dropped from event props.
const ALLOWED_PROP_KEYS = new Set([
  // numeric / boolean / enum only
  'duration_seconds',
  'has_voice',
  'has_music',
  'has_background',
  'has_slideshow',
  'has_video',
  'is_premium',
  'actions_count',
  'actions_completed_count',
  'plan',           // e.g. 'monthly'
  'method',         // e.g. 'stripe'
  'source',         // e.g. 'dashboard', 'settings' (short enum only)
  'subscription_status',        // e.g. 'active', 'canceled'
  'cancellation_effective_date',// short ISO date string (<=32 chars)
]);

const sanitizeProps = (props) => {
  if (!props) return {};
  const clean = {};
  for (const [k, v] of Object.entries(props)) {
    if (ALLOWED_PROP_KEYS.has(k) && v !== null && v !== undefined) {
      // Only allow scalar values (no strings that could carry PII, unless enum-like short)
      if (typeof v === 'boolean' || typeof v === 'number') {
        clean[k] = v;
      } else if (typeof v === 'string' && v.length <= 32) {
        // Only allow short enum-like strings (32-char safety cap)
        clean[k] = v;
      }
    }
  }
  return clean;
};

export const identifyUser = (userId, { is_premium } = {}) => {
  if (!initialized || !userId) return;
  // Identify with internal database ID only. No email/name.
  posthog.identify(String(userId), {
    is_premium: !!is_premium,
  });
};

export const resetUser = () => {
  if (!initialized) return;
  posthog.reset();
};

export const track = (eventName, properties = {}) => {
  if (!ALLOWED_EVENTS.has(eventName)) {
    // Reject any event not on the approved list.
    return;
  }
  if (!initialized) {
    // Diagnostic-only log: event was attempted but PostHog didn't init
    // (missing REACT_APP_POSTHOG_KEY in this deploy).
     
    console.warn('[analytics] Event dropped (posthog not initialized): ' + eventName);
    return;
  }
  posthog.capture(eventName, sanitizeProps(properties));
};

// Convenience typed helpers so callers can't mistype an event name.
export const trackAccountCreated = (props) => track('account_created', props);
export const trackOnboardingCompleted = (props) => track('onboarding_completed', props);
export const trackVisualisationCompleted = (props) => track('visualisation_completed', props);
export const trackDailyRoutineCompleted = (props) => track('daily_routine_completed', props);
export const trackPremiumPageViewed = (props) => track('premium_page_viewed', props);
export const trackSubscriptionStarted = (props) => track('subscription_started', props);
export const trackPremiumCancellationRequested = (props) => track('premium_cancellation_requested', props);
export const trackPremiumCancellationReversed = (props) => track('premium_cancellation_reversed', props);
export const trackPremiumSubscriptionEnded = (props) => track('premium_subscription_ended', props);
